import { useEffect, useState } from 'react';

const WISHLIST_STORAGE_KEY = 'captex_theme3_wishlist';
const CART_STORAGE_KEY = 'captex_theme3_cart';

function readStorage(key) {
    if (typeof window === 'undefined') {
        return [];
    }

    try {
        const stored = window.localStorage.getItem(key);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

function writeStorage(key, value) {
    if (typeof window === 'undefined') {
        return;
    }

    window.localStorage.setItem(key, JSON.stringify(value));
}

function normalizeWishlistItem(product) {
    if (!product) {
        return null;
    }

    return {
        ...product,
        slug: product.slug,
        title: product.title || product.name || 'Untitled Product',
        image: product.image || product.hoverImage || product.gallery?.[0]?.src || product.images?.[0]?.src || product.images?.[0] || '',
    };
}

export function useWishlist() {
    const [wishlist, setWishlist] = useState([]);

    useEffect(() => {
        setWishlist(readStorage(WISHLIST_STORAGE_KEY));
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        writeStorage(WISHLIST_STORAGE_KEY, wishlist);
    }, [wishlist]);

    const wishlistCount = wishlist.length;

    const addToWishlist = (product) => {
        const item = normalizeWishlistItem(product);

        if (!item?.slug) {
            return;
        }

        setWishlist((current) => {
            if (current.some((wishlistItem) => wishlistItem.slug === item.slug)) {
                return current;
            }

            return [item, ...current];
        });
    };

    const removeFromWishlist = (slug) => {
        setWishlist((current) => current.filter((item) => item.slug !== slug));
    };

    const toggleWishlist = (product) => {
        const slug = product?.slug;

        if (!slug) {
            return;
        }

        setWishlist((current) => {
            if (current.some((item) => item.slug === slug)) {
                return current.filter((item) => item.slug !== slug);
            }

            const item = normalizeWishlistItem(product);
            return item ? [item, ...current] : current;
        });
    };

    const clearWishlist = () => {
        setWishlist([]);
    };

    const moveToCart = (product) => {
        const item = normalizeWishlistItem(product);

        if (!item?.slug) {
            return;
        }

        setWishlist((current) => current.filter((wishlistItem) => wishlistItem.slug !== item.slug));

        if (typeof window === 'undefined') {
            return;
        }

        const cart = readStorage(CART_STORAGE_KEY);
        const exists = cart.some((cartItem) => cartItem.slug === item.slug);

        if (!exists) {
            cart.unshift(item);
            writeStorage(CART_STORAGE_KEY, cart);
        }
    };

    const isWishlisted = (slug) => wishlist.some((item) => item.slug === slug);

    const add = addToWishlist;

    return {
        wishlist,
        wishlistProducts: wishlist,
        wishlistCount,
        addToWishlist,
        add,
        removeFromWishlist,
        remove: removeFromWishlist,
        toggleWishlist,
        clearWishlist,
        clear: clearWishlist,
        moveToCart,
        isWishlisted,
    };
}
